<template>
  <div class="switch-account-container h-100 w-100 d-flex flex-column bg-white layout-spacing">
    <div class="logo">
      <img src="@/assets/img/closd_logo.svg" width="120" alt="Closd Logo" />
    </div>
    <div class="my-auto text-center px-6">
      <div class="bg-grey-50 mb-8 mx-auto pa-6 rounded-circle d-flex align-center justify-center" style="width: fit-content;">
        <app-material-icon name="close" size="xl" color="grey-600" />
      </div>
      <h1 class="mb-4">
        {{ t('errorTitle') }}
      </h1>
      <i18n-t :keypath="`${TRANSLATION_KEY_PREFIX}.errorWrongUser`" tag="p" scope="global" class="mb-0">
        <template #currentUserInfo>
          <strong>{{ currentUserName + ' - ' + currentUserEmail }}</strong>
        </template>
      </i18n-t>
       <p>{{ t('toSwitchAccount') }}</p>
      <v-row class="mt-8">
        <v-col class="d-flex flex-column-reverse flex-sm-row justify-center row-gap-4 column-gap-4">
          <app-button type="outlined" :to="{ name: ROOMS_ROUTE_NAME, params: { locale: localeLanguage } }">
            {{ t('returnToHomeBtn') }}
          </app-button>

          <app-button v-if="isWrongUserToAdvancedSign" @click="handleLogout">
            {{ t('switchAccountBtn') }}
          </app-button>

          <app-button v-if="isWrongUserToSimpleSign" @click="handleSimpleSignature">
            {{ t('switchAccountBtn') }}
          </app-button>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted } from 'vue'
import { VueCookies } from 'vue-cookies'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useTranslation } from '@/common/utils/translation'
import { getRootDomainFromUrl, SUBDOMAIN_COOKIE_KEY } from '@/common/utils/url'
import i18n from '@/plugins/i18n'
import { LOGOUT_ROUTE_NAME, ROOMS_ROUTE_NAME, SIMPLE_SIGNATURE_ROUTE_NAME } from '@/router/routerData'

const TRANSLATION_KEY_PREFIX = 'Error.views.switchAccount'
const { t } = useTranslation({ keyPrefix: TRANSLATION_KEY_PREFIX })

const router = useRouter()
const route = useRoute()
const store= useStore()
const $cookies = inject<VueCookies>('$cookies');

const customMessage = route.params['customMessage'] || null
const currentUserEmail = store.state.user.profile.email || ''
const currentUserName = store.state.user.profile.fullName || ''
const prevRoute = store.state.routerHistory?.at(-1)

const isWrongUserToSimpleSign = computed(() => customMessage === 'ERR_WRONG_USER_LOGGED_IN')
const isWrongUserToAdvancedSign = computed(() => customMessage === 'ERR_WRONG_SIGNER')
const isAuthent = computed(() => !!store.getters['user/currentUserId'])
const localeLanguage = computed(() => i18n.global.locale.value)

const handleLogout = () => {
  const returnRoute = prevRoute?.fullPath || '/'
  router.push({
    name: LOGOUT_ROUTE_NAME,
    query: {
      ...route.query,
      returnRoute,
    },
  })
}

const handleSimpleSignature = () => {
  const clientAppUrl = import.meta.env.VUE_APP_CLIENT_URL
  const cookieDomain = getRootDomainFromUrl(clientAppUrl)

  $cookies?.remove(SUBDOMAIN_COOKIE_KEY, undefined, cookieDomain)

  const prevRouteParams = prevRoute?.['params'] || {}

  if (prevRoute?.name === SIMPLE_SIGNATURE_ROUTE_NAME) {
    router.push({
      name: SIMPLE_SIGNATURE_ROUTE_NAME,
      params: prevRouteParams,
      query: {
        withoutApiToken: 'true',
      },
    })
  } else {
    handleLogout()
  }
}

onMounted(() => {
// If no user is authenticated but somehow lands on this page, redirect to logout
  if (!isAuthent.value) {
    handleLogout()
  }
})
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.switch-account-container {
  @media #{map-get($display-breakpoints, 'xs')}{
    padding: 0 !important;
  }
}
.logo {
  position: absolute;
  @media #{map-get($display-breakpoints, 'xs')}{
    position: relative;
    border-bottom: 1px solid;
    border-bottom: 1px solid rgb(var(--v-theme-grey-100));
    padding: $spacer * 3 0;
    display: flex;
    justify-content: center;
  }
}
</style>
